import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, User } from 'lucide-react';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { SEO } from '@/components/SEO';

export default function JournalPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const [articlesRes, categoriesRes] = await Promise.all([
        fetch(`${backendUrl}/api/journal/articles?status=published`),
        fetch(`${backendUrl}/api/journal/categories`)
      ]);
      const articlesData = await articlesRes.json();
      setArticles(articlesData);
      setCategories(await categoriesRes.json());
    } catch (err) {
      console.error('Failed to load journal data');
    } finally {
      setLoading(false);
    }
  };

  const featuredArticle = articles.find(a => a.featured);
  const regularArticles = articles.filter(a => !a.featured);
  const filteredArticles = selectedCategory === 'all' 
    ? regularArticles 
    : regularArticles.filter(a => a.category_id === selectedCategory);

  if (loading) return <div className="min-h-screen" />;

  return (
    <div className="pt-28 pb-20">
      <SEO
        title="BAKLIGHT Journal | Production Stories, Insights & Perspectives"
        description="The editorial home for Baklight's stories, case studies, behind-the-scenes insights, and perspectives on filmmaking and production in Mexico."
        path="/journal"
      />

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        {/* Header */}
        <SectionReveal>
          <div className="text-center mb-16">
            <p className="font-mono-meta text-[hsl(var(--bk-accent))] text-sm mb-4">BAKLIGHT JOURNAL</p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-[-0.03em] text-[hsl(var(--bk-fg))] mb-6">
              Stories, Craft & Perspectives
            </h1>
            <p className="font-body text-lg text-[hsl(var(--bk-fg-muted))] max-w-2xl mx-auto">
              The editorial home of our positioning strategy — case studies, production insights, and perspectives on filmmaking.
            </p>
          </div>
        </SectionReveal>

        {/* Featured Article */}
        {featuredArticle && (
          <SectionReveal>
            <Link to={`/journal/${featuredArticle.slug}`} className="block group mb-20">
              <div className="relative aspect-[21/9] rounded-lg overflow-hidden mb-6">
                <img
                  src={featuredArticle.hero_image}
                  alt={featuredArticle.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 p-8">
                  <span className="inline-block font-mono-meta text-xs text-[hsl(var(--bk-accent))] bg-black/50 px-3 py-1 rounded mb-4">
                    {categories.find(c => c.id === featuredArticle.category_id)?.name}
                  </span>
                  <h2 className="font-display text-3xl md:text-4xl text-white mb-3 max-w-3xl">
                    {featuredArticle.title}
                  </h2>
                  <p className="font-body text-white/80 text-lg max-w-2xl">
                    {featuredArticle.excerpt}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">
                <span className="flex items-center gap-2"><User size={14} /> {featuredArticle.author}</span>
                <span className="flex items-center gap-2"><Calendar size={14} /> {new Date(featuredArticle.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                <span className="flex items-center gap-2"><Clock size={14} /> {featuredArticle.reading_time} min read</span>
              </div>
            </Link>
          </SectionReveal>
        )}

        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-12">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`font-mono-meta text-xs px-4 py-2 rounded-full transition-colors ${
              selectedCategory === 'all'
                ? 'bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]'
                : 'bg-[hsl(var(--bk-bg-elev))] text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))]'
            }`}
          >
            ALL
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`font-mono-meta text-xs px-4 py-2 rounded-full transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]'
                  : 'bg-[hsl(var(--bk-bg-elev))] text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))]'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredArticles.map((article, i) => (
            <SectionReveal key={article.id} delay={i * 0.1}>
              <Link to={`/journal/${article.slug}`} className="group block">
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden mb-4">
                  <img
                    src={article.hero_image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <span className="inline-block font-mono-meta text-xs text-[hsl(var(--bk-accent))] mb-2">
                  {categories.find(c => c.id === article.category_id)?.name}
                </span>
                <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-2 group-hover:text-[hsl(var(--bk-accent))] transition-colors">
                  {article.title}
                </h3>
                <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] mb-4 line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">
                  <span>{article.author}</span>
                  <span>•</span>
                  <span>{article.reading_time} min</span>
                </div>
              </Link>
            </SectionReveal>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-20">
            <p className="font-body text-[hsl(var(--bk-fg-muted))]">No articles found in this category yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
